const EXP_LABELS = {
  1: 'Fresher',
  6: '6 months',
  12: '1 year',
  24: '2 years',
  36: '3 years',
  48: '4 years',
  60: '5 years',
  72: '6 years',
  84: '6+ years',
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const COMMUNICATION_LABELS = {
  1: 'Basic communication',
  2: 'Moderate communication',
  3: 'Good communication',
  4: 'Excellent communication',
};

const COURSE_TYPE_LABELS = {
  1: 'Full time',
  2: 'Part time',
};

export function extractCandidateId(slug) {
  if (!slug) return null;
  const id = parseInt(String(slug).replace(/\D/g, ''), 10);
  return Number.isFinite(id) ? id : null;
}

export function buildPublicProfileSlug(user) {
  if (!user?.name || !user?.id) return '';
  return `${user.name.split(' ').join('-')}${user.id}`;
}

export function parseSkills(skillsRaw) {
  if (!skillsRaw) return [];
  try {
    const parsed = typeof skillsRaw === 'string' ? JSON.parse(skillsRaw) : skillsRaw;
    if (Array.isArray(parsed)) return parsed.filter(Boolean);
    if (typeof parsed === 'string') return [parsed];
    return [];
  } catch {
    return typeof skillsRaw === 'string' ? [skillsRaw] : [];
  }
}

export function parseLanguages(user) {
  const raw = user?.languages_known?.language_known;
  if (!raw) return [];
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
  } catch {
    return [];
  }
}

export function getExperienceLabel(months) {
  if (!months && months !== 0) return null;
  const value = months === 0 ? 1 : months;
  return EXP_LABELS[value] || null;
}

export function formatWorkMonth(monthIndex) {
  if (monthIndex == null || monthIndex === '') return '';
  const idx = Number(monthIndex);
  return MONTHS[idx + 1] || '';
}

export function formatWorkRange(fromMonth, fromYear, toMonth, toYear, isCurrent = false) {
  const start = [formatWorkMonth(fromMonth), fromYear].filter(Boolean).join(' ');
  if (!start) return '';
  if (isCurrent) return `${start} – Present`;
  const end = [formatWorkMonth(toMonth), toYear].filter(Boolean).join(' ');
  return end ? `${start} – ${end}` : start;
}

export function getCommunicationLabel(rating) {
  return COMMUNICATION_LABELS[rating] || null;
}

export function getCourseTypeLabel(type) {
  return COURSE_TYPE_LABELS[type] || null;
}

export function resolveJobRoles(career, jobroles = []) {
  if (!career || !jobroles.length) return [];
  const ids = [career.fst_prefered_role, career.snd_prefered_role, career.trd_prefered_role].filter(Boolean);
  const roles = [];
  ids.forEach((id) => {
    const match = jobroles.find((r) => r.value === id || r.role_id === id);
    const label = match?.text || match?.role;
    if (label && !roles.includes(label)) roles.push(label);
  });
  return roles;
}

export function getLocationLabel(user, userCity) {
  const city = userCity?.city_name || user?.personal_details?.user_city?.city_name;
  const sub = user?.personal_details?.sub_location?.sublocation_name;
  const state = userCity?.state?.state_name || user?.personal_details?.user_city?.state?.state_name;
  const parts = [];
  if (city) parts.push(city);
  if (sub && sub !== city) parts.push(sub);
  if (state && !parts.includes(state)) parts.push(state);
  return parts.length ? parts.join(' · ') : null;
}

export function getEducationSummary(user) {
  const hq = user?.high_qualification;
  if (!hq) return null;
  const course = hq.course?.course_name;
  const branch = hq.branch?.branch_name;
  const university = hq.university?.university_name;
  const year = hq.passout_year;
  const marks = hq.aggregate_marks;
  if (!course && !university) return null;
  return {
    title: branch ? `${course} · ${branch}` : course,
    university,
    state: hq.state?.state_name,
    year,
    marks,
    courseType: getCourseTypeLabel(hq.course_type),
  };
}

export function getSchoolQualifications(user) {
  const items = [];
  const xii = user?.xiith_details;
  if (xii?.xiith_school_name) {
    items.push({
      level: '12th',
      school: xii.xiith_school_name,
      board: xii.xiith_board_name,
      year: xii.xiith_passout_year,
      marks: xii.xiith_marks,
    });
  }
  const x = user?.xth_details;
  if (x?.xth_school_name) {
    items.push({
      level: '10th',
      school: x.xth_school_name,
      board: x.xth_board_name,
      year: x.xth_passout_year,
      marks: x.xth_marks,
    });
  }
  return items;
}

export function getNameInitials(name) {
  if (!name) return '?';
  return name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');
}

export function isValidCompany(company) {
  return !!(company?.worked_company_name || company?.designation);
}

export function getValidCurrentCompany(company) {
  return isValidCompany(company) ? company : null;
}

export function getValidPreviousCompanies(companies = []) {
  if (!Array.isArray(companies)) return [];
  return companies.filter(isValidCompany);
}

export function buildAutoTagline({ name, experienceLabel, locationLabel, jobRoles = [], hasHeadline = false }) {
  const parts = [];
  if (experienceLabel) parts.push(experienceLabel);
  if (locationLabel) {
    const city = locationLabel.split(' · ')[0];
    if (city) parts.push(`based in ${city}`);
  }
  if (!hasHeadline && jobRoles.length) {
    const roles = jobRoles.slice(0, 2).join(', ');
    parts.push(`open to ${roles} roles`);
  }
  if (!parts.length) {
    return name ? `${name.split(' ')[0]} is open to opportunities on Hireclap` : null;
  }
  const firstName = name?.split(' ')[0] || 'Candidate';
  return `${firstName} · ${parts.join(' · ')}`;
}

export function getResumeExtension(resumePath) {
  if (!resumePath) return '';
  const filename = String(resumePath).split('/').pop() || '';
  const parts = filename.split('.');
  if (parts.length < 2) return '';
  return parts.pop().toLowerCase();
}

export function getResumeViewerType(resumePath) {
  const ext = getResumeExtension(resumePath);
  if (ext === 'pdf') return 'pdf';
  if (ext === 'doc' || ext === 'docx') return 'word';
  return null;
}

export function getResumeFileName(resumePath) {
  if (!resumePath) return 'Resume';
  const raw = String(resumePath).split('/').pop() || 'Resume';
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

export function getOfficeEmbedUrl(fileUrl) {
  return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`;
}
